import React from 'react';
import clsx from 'clsx';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'filled' | 'outlined' | 'text' | 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'filled', size = 'md', ...props }, ref) => {
    const base = clsx(
      'inline-flex items-center justify-center gap-2',
      'font-medium rounded-full',
      'transition-all duration-200 ease-in-out',
      'select-none cursor-pointer',
      'ripple',
      'relative overflow-hidden',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1565C0] focus-visible:ring-offset-2',
    );

    const variants: Record<string, string> = {
      filled:
        'bg-[#1565C0] text-white hover:bg-[#0d47a1] active:bg-[#0a3d91] shadow-md-1 hover:shadow-md-2',
      primary:
        'bg-[#1565C0] text-white hover:bg-[#0d47a1] active:bg-[#0a3d91] shadow-md-1 hover:shadow-md-2',
      secondary:
        'bg-[#6200EA] text-white hover:bg-[#4a00c7] active:bg-[#3700a8] shadow-md-1 hover:shadow-md-2',
      outlined:
        'bg-transparent text-[#1565C0] dark:text-[#90CAF9] border-2 border-[#1565C0] dark:border-[#90CAF9] hover:bg-[#E3F2FD] dark:hover:bg-[#1565C0]/20',
      text:
        'bg-transparent text-[#1565C0] dark:text-[#90CAF9] hover:bg-[#E3F2FD] dark:hover:bg-[#1565C0]/20',
      danger:
        'bg-[#B00020] text-white hover:bg-[#880019] active:bg-[#6e0014] shadow-md-1 hover:shadow-md-2',
    };

    const sizes: Record<string, string> = {
      sm: 'px-4 py-1.5 text-sm',
      md: 'px-6 py-2.5 text-sm',
      lg: 'px-8 py-3.5 text-base',
    };

    return (
      <button
        ref={ref}
        className={clsx(base, variants[variant] ?? variants.filled, sizes[size], className)}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
